import React, { useState } from "react";
import { db, Posts } from "@/db";
import { IssueType, PostType } from "@/core/types/contentTypes";
import { IndexableType } from "dexie";
import { wrapper } from "@/core/store/store";
import { toast } from "sonner";
import { Toast } from "@/components/ui-kit/Toast";
import IssueApi from "@/connectors/fcWordpress/apiCallers/IssueApi";
import { useOfflinePostContent } from "@/hooks/indexeddb/useOfflinePostContent";

type OfflineIssueContent = {
  downloadIssue: (issue: IssueType) => Promise<void>;
  getDownloadedIssue: (id: number) => Promise<IssueType | undefined>;
  getDownloadedIssueBySlug: (slug: string) => Promise<IssueType | undefined>;
  deleteIssue: (id: number) => void;
  downloading: boolean;
  saveOrDelete: (issue: IssueType) => Promise<undefined | boolean>;
  isDownloaded: (id: number) => Promise<boolean>;
};

export const useOfflineIssueContent = (): OfflineIssueContent => {
  const { store } = wrapper.useWrappedStore({});
  const [downloading, setDownloading] = useState(false);
  const { saveMissing, deleteAllFromByIds } = useOfflinePostContent();

  const downloadIssue = async (issue: IssueType, withToast = true) => {
    if (withToast) {
      // Unfortunately, we cannot set duration to infinity, so we have to set it to a very high number
      toast.custom((t) => <Toast id={t} text="Probíhá stahování..." withLoader />, {
        duration: 1000000,
        id: issue.id.toString(),
      });
    }

    try {
      const api = new IssueApi({
        slug: issue.slug,
        language: process.env.NEXT_PUBLIC_DEFAULT_ROUTING_LANGUAGE_CODE!,
      });

      await api.fetchSingleIssue();
      let fullIssue = api.result as IssueType;
      await saveMissing(fullIssue.articles!);

      fullIssue.articleIds = fullIssue.articles?.map((i) => (i as PostType).id);
      delete fullIssue.articles;
      delete fullIssue.yoastHeadJson;

      await db.issues.put({ id: fullIssue.id, slug: issue.slug, issue: fullIssue }, fullIssue.id);

      if (withToast) {
        toast.dismiss(issue.id.toString());
        toast.custom((t) => <Toast id={t} text="Stahování dokončeno" showRedirectToDownloadedIssues />, {
          duration: 2000,
        });
      }
    } catch (error) {
      if (withToast) {
        toast.dismiss(issue.id.toString());
        toast.custom((t) => <Toast id={t} text="Stahování se nepodařilo" />, {
          duration: 2000,
        });
      }
    }

    return;
  };

  const getDownloadedIssue = async (id: number) => {
    if (!id) return undefined;
    const issueRecord = await db.issues?.get(id);

    if (issueRecord) {
      let issue = issueRecord?.issue!;

      const posts = await db.posts.where("id").anyOf(issue?.articleIds!).toArray();
      issue.articles = posts.map((i: Posts) => i.post);

      return issue;
    }
    return undefined;
  };

  const getDownloadedIssueBySlug = async (slug: string) => {
    const issueRecord = await db.issues?.where({ slug }).first();

    if (!issueRecord) return undefined;

    let issue = issueRecord?.issue!;

    const posts = await db.posts.where("id").anyOf(issue.articleIds!).toArray();
    issue.articles = posts.map((i: Posts) => i.post);

    return issue;
  };

  const deleteIssue = async (id: number, withToast = true) => {
    if (withToast) {
      toast.custom((t) => <Toast id={t} duration={3000} text="Probíhá mazání..." />, {
        duration: 3000,
        onAutoClose() {
          toast.custom((t) => <Toast id={t} text="Všechny soubory byly smazány" />, {
            duration: 2000,
          });
        },
      });
    }

    const issue = await getDownloadedIssue(id);

    await deleteAllFromByIds(issue?.articleIds!);
    return db.issues.delete(id);
  };

  const saveOrDelete = async (issue: IssueType) => {
    if (!!(await getDownloadedIssue(issue.id))) {
      deleteIssue(issue.id);
      return;
    }

    await downloadIssue(issue);
    return true;
  };

  const isDownloaded = async (id: number) => {
    const track = await getDownloadedIssue(id);
    return !!track;
  };

  return {
    downloadIssue,
    getDownloadedIssue,
    getDownloadedIssueBySlug,
    deleteIssue,
    downloading,
    saveOrDelete,
    isDownloaded,
  };
};
